/**
 * QualityStandardFormDialog — bulk-edit `standard_value` cho 1 parameter
 * theo master quality list (`public.list_and_valo where is_deduction_show=1`).
 *
 * Bám Access `frmFactoryQryDeductionStandardAdd`:
 *   - Header lock: factory_code, parameter_code, parameter_name (caller set).
 *   - Subform list = tmp_deduction_standard pre-filled từ
 *     FactoryQryDeductionStandardDetail WHERE is_deduction_show=1.
 *   - btnSave_Click (line 1025-1072): loop tmp, UPDATE if exists else INSERT.
 *   - btnCancel_Click: Close without persist.
 *
 * Rows được BE trả về đã merge sẵn (master + existing). User CHỈ edit
 * `standard_value`; quality_code locked (master-driven).
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import { FORM_GRID_3COL, TEXT_TRUNCATE } from '@/theme/responsive'

export interface QualityStandardBulkRow {
  quality_code: string
  standard_value: number | null
}

interface QualityStandardFormDialogProps {
  open: boolean
  loading: boolean
  saving: boolean
  canEdit: boolean
  factoryCode: string
  parameterCode: string
  parameterName: string
  fieldName: string
  rows: QualityStandardBulkRow[]
  onClose: () => void
  onSave: (rows: QualityStandardBulkRow[]) => void
}

interface DraftRow {
  quality_code: string
  standard_value: string
}

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.4, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
}

// Skill `/convert-form` readonly bg standard — neutral gray.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
  },
}

const stringifyValue = (v: number | null): string =>
  v == null ? '' : String(v)

export default function QualityStandardFormDialog({
  open,
  loading,
  saving,
  canEdit,
  factoryCode,
  parameterCode,
  parameterName,
  fieldName,
  rows,
  onClose,
  onSave,
}: QualityStandardFormDialogProps) {
  const [draft, setDraft] = useState<DraftRow[]>([])
  const [validationError, setValidationError] = useState('')

  // Hydrate draft khi rows từ BE thay đổi. BE đã merge master + existing →
  // FE chỉ cần stringify standard_value cho TextField (mirror Access tmp-load).
  useEffect(() => {
    if (!open) return
    setDraft(
      rows.map((r) => ({
        quality_code: r.quality_code,
        standard_value: stringifyValue(r.standard_value),
      })),
    )
    setValidationError('')
  }, [open, rows])

  const handleValueChange = (idx: number, value: string) => {
    setDraft((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], standard_value: value }
      return next
    })
  }

  /** Numeric validation per row (Access Form_Error line 2300-2303: number
   *  fields show "Please enter number" cho input không parse được). */
  const handleSave = () => {
    if (!canEdit || saving) return
    for (let i = 0; i < draft.length; i += 1) {
      const r = draft[i]
      const sv = r.standard_value.trim()
      if (sv !== '' && !Number.isFinite(Number(sv))) {
        setValidationError(`${r.quality_code}: Standard value must be a number`)
        return
      }
    }
    setValidationError('')
    onSave(
      draft.map((r) => ({
        quality_code: r.quality_code,
        standard_value: r.standard_value.trim() === '' ? null : Number(r.standard_value),
      })),
    )
  }

  const headerLabel = useMemo(
    () =>
      parameterName
        ? `Edit — ${parameterCode} · ${parameterName}`
        : `Edit — ${parameterCode}`,
    [parameterCode, parameterName],
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(720px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          component="span"
          noWrap
          title={headerLabel}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {headerLabel}
        </Typography>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.5 }}
        >
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canEdit || saving || loading}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          ...compactInputSx,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          pt: 1.5,
        }}
      >
        {/* Locked header — caller pre-fills, user không sửa. */}
        <Box sx={{ ...FORM_GRID_3COL, mb: 1.25 }}>
          <TextField
            label="Parameter"
            size="small"
            fullWidth
            value={parameterCode}
            InputProps={{ readOnly: true }}
            disabled
            sx={readonlyFieldSx}
          />
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={parameterName}
            InputProps={{ readOnly: true }}
            disabled
            sx={readonlyFieldSx}
          />
          <TextField
            label="Field"
            size="small"
            fullWidth
            value={fieldName}
            InputProps={{ readOnly: true }}
            disabled
            sx={readonlyFieldSx}
          />
        </Box>

        {validationError ? (
          <Alert severity="error" sx={{ mb: 1, py: 0.25 }}>
            {validationError}
          </Alert>
        ) : null}

        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, fontSize: '0.82rem', mb: 0.5 }}
        >
          Standards by Quality Code
        </Typography>

        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress size={20} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Loading detail…
            </Typography>
          </Stack>
        ) : draft.length === 0 ? (
          <Alert severity="info" sx={{ py: 0.5 }}>
            No visible quality codes for this factory. Configure master list before editing.
          </Alert>
        ) : (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {/* Sticky header cells cần background opaque + z-index để
                      khi body scroll, rows không "lọt" qua header. */}
                  <TableCell
                    sx={{
                      width: '50%',
                      bgcolor: 'background.paper',
                      fontWeight: 700,
                      borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
                      zIndex: 2,
                    }}
                  >
                    Quality Code
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '50%',
                      bgcolor: 'background.paper',
                      fontWeight: 700,
                      borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
                      zIndex: 2,
                    }}
                  >
                    Standard Value
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {draft.map((r, idx) => (
                  <TableRow key={r.quality_code} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{r.quality_code}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        value={r.standard_value}
                        onChange={(e) => handleValueChange(idx, e.target.value)}
                        disabled={!canEdit || saving}
                        placeholder="—"
                        sx={compactInputSx}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      </DialogContent>
    </Dialog>
  )
}
